"use client";

import type React from "react";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Bar, Line, Pie } from "react-chartjs-2";
import AdminService from "../../../services/admin.services";

// Import React Icons
import { FiUsers, FiShoppingBag, FiPackage } from "react-icons/fi";
import { BiStore, BiCalendar, BiTrendingUp } from "react-icons/bi";
import { HiUserGroup } from "react-icons/hi";
import { IoMdArrowDropup, IoMdArrowDropdown } from "react-icons/io";
import { MdOutlineError } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

// Add the necessary imports for Chart.js
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  ArcElement,
} from "chart.js";
import FoodLoader from "src/components/Loader";

// Register all required Chart.js components
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  ArcElement
);

const Statistics: React.FC = () => {
  const [statsData, setStatsData] = useState<any>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const adminService = new AdminService();
        const response = await adminService.getAppStats();
        setStatsData(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Calculate percentage changes (mock data for demonstration)
  const getPercentChange = (value: number) => {
    return Math.floor(Math.random() * 20) - 10; // Random value between -10 and 10
  };

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 10,
        titleFont: {
          size: 13,
        },
        bodyFont: {
          size: 12,
        },
      },
    },
    animation: {
      duration: 1000,
    },
  };

  const agentsData = {
    labels: ["Agents"],
    datasets: [
      {
        label: "Number of Agents",
        data: [statsData?.agents || 0],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  const ordersData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Orders",
        data: [
          statsData?.orders * 0.7 || 0,
          statsData?.orders * 0.8 || 0,
          statsData?.orders * 0.6 || 0,
          statsData?.orders * 0.9 || 0,
          statsData?.orders * 0.75 || 0,
          statsData?.orders || 0,
        ],
        backgroundColor: "rgba(153, 102, 255, 0.2)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const parcelsData = {
    labels: ["Delivered", "In Transit", "Pending"],
    datasets: [
      {
        label: "Parcel Status",
        data: [
          statsData?.parcels * 0.7 || 0,
          statsData?.parcels * 0.2 || 0,
          statsData?.parcels * 0.1 || 0,
        ],
        backgroundColor: [
          "rgba(75, 192, 192, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ],
        borderColor: [
          "rgba(75, 192, 192, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const restaurantsData = {
    labels: ["Active", "Inactive"],
    datasets: [
      {
        label: "Restaurant Status",
        data: [
          statsData?.restaurants * 0.85 || 0,
          statsData?.restaurants * 0.15 || 0,
        ],
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(201, 203, 207, 0.6)",
        ],
        borderColor: ["rgba(54, 162, 235, 1)", "rgba(201, 203, 207, 1)"],
        borderWidth: 1,
      },
    ],
  };

  const usersData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "User Growth",
        data: [
          statsData?.users * 0.5 || 0,
          statsData?.users * 0.6 || 0,
          statsData?.users * 0.7 || 0,
          statsData?.users * 0.8 || 0,
          statsData?.users * 0.9 || 0,
          statsData?.users || 0,
        ],
        backgroundColor: "rgba(255, 206, 86, 0.2)",
        borderColor: "rgba(255, 206, 86, 1)",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const comparisonData = {
    labels: ["Agents", "Orders", "Parcels", "Restaurants", "Users"],
    datasets: [
      {
        label: "Current Period",
        data: [
          statsData?.agents || 0,
          statsData?.orders || 0,
          statsData?.parcels || 0,
          statsData?.restaurants || 0,
          statsData?.users || 0,
        ],
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Previous Period",
        data: [
          (statsData?.agents || 0) * 0.8,
          (statsData?.orders || 0) * 0.7,
          (statsData?.parcels || 0) * 0.9,
          (statsData?.restaurants || 0) * 0.85,
          (statsData?.users || 0) * 0.75,
        ],
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
    ],
  };

  // Stat card component for reusability
  const StatCard = ({
    title,
    value,
    icon,
    percentChange,
  }: {
    title: string;
    value: number;
    icon: React.ReactNode;
    percentChange: number;
  }) => (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow duration-300 border border-gray-100 w-full">
      {/* Icon + Change Badge */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="p-2 rounded-lg bg-blue-50 text-blue-600 w-fit">
          {icon}
        </div>
        <div
          className={`text-xs font-medium px-2 py-1 rounded-full flex items-center ${
            percentChange >= 0
              ? "bg-green-50 text-green-600"
              : "bg-red-50 text-red-600"
          }`}
        >
          {percentChange >= 0 ? (
            <IoMdArrowDropup className="w-4 h-4" />
          ) : (
            <IoMdArrowDropdown className="w-4 h-4" />
          )}
          {Math.abs(percentChange)}%
        </div>
      </div>

      {/* Title + Value */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
        <h3 className="text-gray-500 text-sm sm:text-base font-medium truncate">
          {title}
        </h3>
        <div className="text-xl sm:text-2xl font-bold text-gray-800">
          {value.toLocaleString()}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between mb-8">
          {/* Left: Heading and description */}
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-600">
              Dashboard Analytics
            </h1>
            <p className="text-gray-500 mt-2 text-base sm:text-lg">
              Overview of your application statistics
            </p>
          </div>

          {/* Right: Date pickers */}
          <div className="flex flex-row gap-3 w-full sm:w-auto">
            {/* Start Date Picker */}
            <div className="relative w-full sm:w-auto">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) =>
                  setStartDate(date ?? undefined)
                }
                selectsStart
                startDate={startDate}
                endDate={endDate}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-48"
                placeholderText="Start Date"
              />
              <BiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>

            {/* End Date Picker */}
            <div className="relative w-full sm:w-auto">
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => setEndDate(date ?? undefined)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate || undefined}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-48"
                placeholderText="End Date"
              />
              <BiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
            <FoodLoader
              size="md"
              text="Fetching Statistics"
              theme="amber"
              className="scale-75"
            />
          </div>
        ) : statsData ? (
          <>
            {/* Stats Overview Cards */}
            <div className="overflow-x-auto whitespace-nowrap px-2">
              <div className="flex gap-4 min-w-max">
                <StatCard
                  title="Total Users"
                  value={statsData.users}
                  icon={<FiUsers className="w-5 h-5" />}
                  percentChange={getPercentChange(statsData.users)}
                />
                <StatCard
                  title="Food Orders"
                  value={statsData.orders}
                  icon={<FiShoppingBag className="w-5 h-5" />}
                  percentChange={getPercentChange(statsData.orders)}
                />
                <StatCard
                  title="Parcel Orders"
                  value={statsData.parcels}
                  icon={<FiPackage className="w-5 h-5" />}
                  percentChange={getPercentChange(statsData.parcels)}
                />
                <StatCard
                  title="Restaurants"
                  value={statsData.restaurants}
                  icon={<BiStore className="w-5 h-5" />}
                  percentChange={getPercentChange(statsData.restaurants)}
                />
                <StatCard
                  title="Agents"
                  value={statsData.agents}
                  icon={<HiUserGroup className="w-5 h-5" />}
                  percentChange={getPercentChange(statsData.agents)}
                />
              </div>
            </div>

            {/* Main Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Order Trends
                  </h2>
                  <div className="flex items-center text-sm text-gray-500">
                    <BiTrendingUp className="w-4 h-4 mr-1 text-green-500" />
                    <span className="text-green-500 font-medium">+12.5%</span>
                    <span className="ml-1">vs last period</span>
                  </div>
                </div>
                <div className="h-80">
                  <Line data={ordersData} options={chartOptions} />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-800">
                    User Growth
                  </h2>
                  <div className="flex items-center text-sm text-gray-500">
                    <BiTrendingUp className="w-4 h-4 mr-1 text-green-500" />
                    <span className="text-green-500 font-medium">+8.3%</span>
                    <span className="ml-1">vs last period</span>
                  </div>
                </div>
                <div className="h-80">
                  <Line data={usersData} options={chartOptions} />
                </div>
              </div>
            </div>

            {/* Secondary Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                <h2 className="text-lg font-semibold text-gray-800 mb-6">
                  Parcel Status
                </h2>
                <div className="h-64">
                  <Pie data={parcelsData} options={chartOptions} />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
                <h2 className="text-lg font-semibold text-gray-800 mb-6">
                  Restaurant Status
                </h2>
                <div className="h-64">
                  <Pie data={restaurantsData} options={chartOptions} />
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 md:col-span-2 lg:col-span-1 overflow-x-auto">
                <h2 className="text-lg font-semibold text-gray-800 mb-6">
                  Agent Performance
                </h2>
                <div className="h-64">
                  <Bar data={agentsData} options={chartOptions} />
                </div>
              </div>
            </div>

            {/* Comparison Chart */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8 overflow-x-auto">
              <h2 className="text-lg font-semibold text-gray-800 mb-6">
                Period Comparison
              </h2>
              <div className="h-96">
                <Bar data={comparisonData} options={chartOptions} />
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center">
            <div className="text-gray-400 mb-4">
              <MdOutlineError className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No Data Available
            </h3>
            <p className="text-gray-500">
              We couldn't load the statistics data. Please try again later.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;
